import { css } from 'lit';

export const styles = css`
  :host {
    /* --asc-button-display lets a parent shadow (e.g. asc-navbar mobile actions area)
       override the host display via CSS custom property inheritance, which is the
       only reliable cross-shadow-boundary styling mechanism. */
    display: var(--asc-button-display, inline-block);
    vertical-align: middle;
  }

  /* [data-navbar-mobile] is set directly by asc-navbar._syncActionStyles() when
     the navbar is in mobile mode. Attribute selectors on :host have the highest
     shadow-internal specificity and require no inheritance or cascade tricks. */
  :host([data-navbar-mobile]) {
    display: block;
    width: 100%;
  }

  [hidden] {
    display: none !important;
  }

  /* ═══════════════════════════════════════════════════════════
     BASE
     Shared defaults for every button variant and size.
     ═══════════════════════════════════════════════════════════ */
  .base {
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    /* Default gap (sm/md). lg/xl override via their size rules. */
    gap: var(--asc-button-icon-gap, var(--asc-spacing-xs));
    font-family: var(--asc-font-family-body);
    font-weight: var(--asc-font-weight-semibold);
    border: var(--asc-border-width) solid transparent;
    border-radius: var(--asc-button-border-radius, var(--asc-radius-sm));
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    transition:
      background-color var(--asc-transition-fast),
      border-color var(--asc-transition-fast),
      color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
    user-select: none;
    box-sizing: border-box;
    position: relative;
    /* line-height:1 collapses text to its font-size height so that
       min-height (set per size) exclusively controls button height,
       matching Figma's fixed-height auto-layout frames. */
    line-height: 1;
  }

  /* ═══════════════════════════════════════════════════════════
     SIZES
     Each rule sets: gap override (if different from base),
     min-height, padding, and font-size.
     Figma-verified pixel values (sm: 36px, md: 40px, lg: 44px, xl: 48px).
     ═══════════════════════════════════════════════════════════ */
  .base--sm {
    /* gap inherits base 4px (spacing-xs) ✓ */
    min-height: calc(var(--asc-spacing-4xl) + var(--asc-spacing-xs)); /* 36px */
    padding:
      var(--asc-button-padding-y-sm, var(--asc-spacing-md))
      var(--asc-button-padding-x-sm, var(--asc-spacing-lg));
    font-size: var(--asc-button-font-size-sm, var(--asc-font-size-text-sm));
  }

  .base--md {
    /* gap inherits base 4px (spacing-xs) ✓ */
    min-height: var(--asc-spacing-5xl); /* 40px */
    /* Figma: py=10px, px=14px — no matching spacing tokens, use raw values */
    padding:
      var(--asc-button-padding-y-md, 10px)
      var(--asc-button-padding-x-md, 14px);
    font-size: var(--asc-button-font-size-md, var(--asc-font-size-text-sm));
  }

  .base--lg {
    /* Figma: gap steps up to spacing-sm (6px) from lg onwards */
    gap: var(--asc-button-icon-gap, var(--asc-spacing-sm));
    min-height: calc(var(--asc-spacing-5xl) + var(--asc-spacing-xs)); /* 44px */
    /* Figma: py=10px (no token), px=16px (spacing-xl) */
    padding:
      var(--asc-button-padding-y-lg, 10px)
      var(--asc-button-padding-x-lg, var(--asc-spacing-xl));
    font-size: var(--asc-button-font-size-lg, var(--asc-font-size-text-md));
  }

  .base--xl {
    gap: var(--asc-button-icon-gap, var(--asc-spacing-sm));
    min-height: var(--asc-spacing-6xl); /* 48px */
    /* Figma: py=12px (spacing-lg), px=18px (no token) */
    padding:
      var(--asc-button-padding-y-xl, var(--asc-spacing-lg))
      var(--asc-button-padding-x-xl, 18px);
    font-size: var(--asc-button-font-size-xl, var(--asc-font-size-text-md));
  }

  /* ═══════════════════════════════════════════════════════════
     ICON-ONLY
     Strip padding and set min-width = min-height so the button
     is a perfect square for every size. The icon centers via
     flex align-items / justify-content. The size rules still
     supply min-height so we only need min-width here.
     ═══════════════════════════════════════════════════════════ */
  .base--icon-only {
    padding: 0;
  }

  .base--icon-only.base--sm {
    min-width: calc(var(--asc-spacing-4xl) + var(--asc-spacing-xs)); /* 36px */
  }

  .base--icon-only.base--md {
    min-width: var(--asc-spacing-5xl); /* 40px */
  }

  .base--icon-only.base--lg {
    min-width: calc(var(--asc-spacing-5xl) + var(--asc-spacing-xs)); /* 44px */
  }

  .base--icon-only.base--xl {
    min-width: var(--asc-spacing-6xl); /* 48px */
  }

  /* ═══════════════════════════════════════════════════════════
     STRUCTURAL OVERRIDES
     Border-radius, link shape, and elevation rules that differ
     from the base defaults but apply across multiple states.
     ═══════════════════════════════════════════════════════════ */

  /* Destructive theme: larger radius (radius-md) distinguishes danger actions */
  .base--theme-destructive {
    border-radius: var(--asc-button-border-radius, var(--asc-radius-md));
  }

  /* Icon-only: tight radius (overrides destructive theme above) */
  .base--icon-only {
    border-radius: var(--asc-button-border-radius, var(--asc-radius-xs));
  }

  /* Link variants: no padding, radius-xs, medium weight, content-height only.
     min-height:0 overrides the size rules so the button shrinks to its
     line-height. line-height is overridden from 1 to the actual value so
     text-only links render at the correct height (20px for sm/md, 24px for lg/xl). */
  .base--link-color,
  .base--link-gray {
    padding: 0;
    min-height: 0;
    border-radius: var(--asc-button-border-radius, var(--asc-radius-xs));
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm); /* sm/md: 20px */
  }

  .base--link-color.base--lg,
  .base--link-color.base--xl,
  .base--link-gray.base--lg,
  .base--link-gray.base--xl {
    line-height: var(--asc-line-height-text-md); /* 24px */
  }

  /* Primary + secondary carry an outer drop shadow (shadow-xs) */
  .base--primary,
  .base--secondary {
    box-shadow: 0 1px 2px 0 var(--asc-shadow-xs);
  }

  /* Primary: the visible 2px white ring is an inset box-shadow, not a CSS
     border. CSS border composites against what is OUTSIDE the element (the
     page background), so rgba(255,255,255,0.12) would render as near-white on
     a white page and as a visible ring gap of transparent on dark pages.
     Figma stroke-inside composites against the fill — an inset box-shadow
     with 0 blur matches this exactly. The base 1px border is kept at its
     default (transparent) so no border gap appears. No padding correction is
     needed because border-width stays at 1px (the same as all other variants). */
  .base--primary {
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      inset 0 0 0 2px rgba(255, 255, 255, 0.12);
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANT: PRIMARY
     ═══════════════════════════════════════════════════════════ */
  .base--primary.base--theme-default {
    background-color: var(--asc-button-primary-bg, var(--asc-bg-brand-solid));
    color: var(--asc-button-primary-fg, var(--asc-color-base-white));
  }

  .base--primary.base--theme-default:hover {
    background-color: var(--asc-button-primary-bg-hover, var(--asc-bg-brand-solid_hover));
  }

  .base--primary.base--theme-destructive {
    background-color: var(--asc-button-primary-bg-destructive, var(--asc-bg-error-solid));
    color: var(--asc-button-primary-fg-destructive, var(--asc-color-base-white));
  }

  .base--primary.base--theme-destructive:hover {
    background-color: var(--asc-button-primary-bg-destructive-hover, var(--asc-bg-error-solid_hover));
  }

  .base--primary.base--theme-plum {
    background-color: var(--asc-accent-plum-bg-strong-hover, #524887);
    color: var(--asc-button-primary-fg, var(--asc-color-base-white));
  }

  .base--primary.base--theme-plum:hover {
    background-color: var(--asc-accent-plum-bg-strong, #362f5b);
  }

  .base--primary.base--theme-berry {
    background-color: var(--asc-accent-berry-bg-strong-hover, #af4d7e);
    color: var(--asc-button-primary-fg, var(--asc-color-base-white));
  }

  .base--primary.base--theme-berry:hover {
    background-color: var(--asc-accent-berry-bg-strong, #55223b);
  }

  .base--primary.base--theme-sky {
    background-color: var(--asc-accent-sky-bg-strong-hover, #037b97);
    color: var(--asc-button-primary-fg, var(--asc-color-base-white));
  }

  .base--primary.base--theme-sky:hover {
    background-color: var(--asc-accent-sky-bg-strong, #003d4d);
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANT: SECONDARY
     ═══════════════════════════════════════════════════════════ */
  .base--secondary.base--theme-default {
    background-color: var(--asc-button-secondary-bg, var(--asc-bg-primary));
    border-color: var(--asc-button-secondary-border, var(--asc-border-primary));
    color: var(--asc-button-secondary-fg, var(--asc-text-secondary));
  }

  .base--secondary.base--theme-default:hover {
    background-color: var(--asc-button-secondary-bg-hover, var(--asc-bg-primary_hover));
    color: var(--asc-button-secondary-fg-hover, var(--asc-text-secondary_hover));
  }

  .base--secondary.base--theme-destructive {
    background-color: var(--asc-button-secondary-bg-destructive, var(--asc-bg-primary));
    border-color: var(--asc-button-secondary-border-destructive, var(--asc-border-error_subtle));
    color: var(--asc-button-secondary-fg-destructive, var(--asc-text-error-primary));
  }

  .base--secondary.base--theme-destructive:hover {
    background-color: var(--asc-button-secondary-bg-destructive-hover, var(--asc-bg-error-primary));
    color: var(--asc-button-secondary-fg-destructive-hover, var(--asc-text-error-primary_hover));
  }

  .base--secondary.base--theme-plum {
    background-color: var(--asc-bg-primary, #ffffff);
    border-color: var(--asc-accent-plum-border, #d8d7e3);
    color: var(--asc-accent-plum-text-secondary, #362f5b);
  }

  .base--secondary.base--theme-plum:hover {
    background-color: var(--asc-accent-plum-bg-hover, #e9e9ef);
  }

  .base--secondary.base--theme-berry {
    background-color: var(--asc-bg-primary, #ffffff);
    border-color: var(--asc-accent-berry-border, #ebd1db);
    color: var(--asc-accent-berry-text-secondary, #55223b);
  }

  .base--secondary.base--theme-berry:hover {
    background-color: var(--asc-accent-berry-bg-hover, #f2e6eb);
  }

  .base--secondary.base--theme-sky {
    background-color: var(--asc-bg-primary, #ffffff);
    border-color: var(--asc-accent-sky-border, #bae0ee);
    color: var(--asc-accent-sky-text-secondary, #003d4d);
  }

  .base--secondary.base--theme-sky:hover {
    background-color: var(--asc-accent-sky-bg-hover, #dbedf4);
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANT: TERTIARY
     ═══════════════════════════════════════════════════════════ */
  .base--tertiary.base--theme-default {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-button-tertiary-fg, var(--asc-text-tertiary));
  }

  .base--tertiary.base--theme-default:hover {
    background-color: var(--asc-button-tertiary-bg-hover, var(--asc-bg-primary_hover));
    color: var(--asc-button-tertiary-fg-hover, var(--asc-text-tertiary_hover));
  }

  .base--tertiary.base--theme-destructive {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-button-tertiary-fg-destructive, var(--asc-text-error-primary));
  }

  .base--tertiary.base--theme-destructive:hover {
    background-color: var(--asc-button-tertiary-bg-destructive-hover, var(--asc-bg-error-primary));
  }

  .base--tertiary.base--theme-plum {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-accent-plum-text-secondary, #362f5b);
  }

  .base--tertiary.base--theme-plum:hover {
    background-color: var(--asc-accent-plum-bg-hover, #e9e9ef);
    color: var(--asc-accent-plum-fg-hover, #524887);
  }

  .base--tertiary.base--theme-berry {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-accent-berry-text-secondary, #55223b);
  }

  .base--tertiary.base--theme-berry:hover {
    background-color: var(--asc-accent-berry-bg-hover, #f2e6eb);
    color: var(--asc-accent-berry-fg-hover, #af4d7e);
  }

  .base--tertiary.base--theme-sky {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-accent-sky-text-secondary, #003d4d);
  }

  .base--tertiary.base--theme-sky:hover {
    background-color: var(--asc-accent-sky-bg-hover, #dbedf4);
    color: var(--asc-accent-sky-fg-hover, #037b97);
  }

  /* Focused: Figma shows bg-primary (white) applied to both themes */
  .base--tertiary:focus-visible {
    background-color: var(--asc-bg-primary);
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANT: LINK-COLOR
     ═══════════════════════════════════════════════════════════ */
  .base--link-color {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-button-link-color-fg, var(--asc-text-brand-secondary));
  }

  .base--link-color:hover {
    color: var(--asc-button-link-color-fg-hover, var(--asc-text-brand-secondary_hover));
  }

  /* Destructive: error-primary colour + semibold weight (Figma: same as secondary fg) */
  .base--link-color.base--theme-destructive {
    color: var(--asc-text-error-primary);
    font-weight: var(--asc-font-weight-semibold);
  }

  .base--link-color.base--theme-destructive:hover {
    color: var(--asc-text-error-primary);
  }

  .base--link-color.base--theme-plum {
    color: var(--asc-accent-plum-fg-hover, #524887);
  }

  .base--link-color.base--theme-plum:hover {
    color: var(--asc-accent-plum-fg-hover, #524887);
  }

  .base--link-color.base--theme-berry {
    color: var(--asc-accent-berry-fg-hover, #af4d7e);
  }

  .base--link-color.base--theme-berry:hover {
    color: var(--asc-accent-berry-fg-hover, #af4d7e);
  }

  .base--link-color.base--theme-sky {
    color: var(--asc-accent-sky-fg-hover, #037b97);
  }

  .base--link-color.base--theme-sky:hover {
    color: var(--asc-accent-sky-fg-hover, #037b97);
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANT: LINK-GRAY
     ═══════════════════════════════════════════════════════════ */
  .base--link-gray {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-button-link-gray-fg, var(--asc-text-tertiary));
  }

  .base--link-gray:hover {
    color: var(--asc-button-link-gray-fg-hover, var(--asc-text-tertiary_hover));
  }

  /* ═══════════════════════════════════════════════════════════
     FOCUS RINGS
     Multi-layer box-shadow matching Figma focus states:
       tertiary/link (base)     : 2px white + 4px teal
       primary/secondary        : shadow-xs + 2px white + 4px teal
       destructive tertiary/link: 2px white + 4px error
       destructive secondary    : shadow-xs + 2px white + 4px error
       destructive primary      : shadow-xs + 4px error (no white ring)
     ═══════════════════════════════════════════════════════════ */
  .base:focus-visible {
    outline: none;
    /* Default: tertiary + link variants (both themes default) */
    box-shadow:
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring, var(--asc-focus-ring));
  }

  /* Secondary (default theme): shadow-xs + white ring + teal ring */
  .base--secondary:focus-visible {
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring, var(--asc-focus-ring));
  }

  /* Primary (default theme): same as secondary + preserve the inset highlight */
  .base--primary:focus-visible {
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      inset 0 0 0 2px rgba(255, 255, 255, 0.12),
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring, var(--asc-focus-ring));
  }

  /* Destructive theme — tertiary + link: white ring + error ring */
  .base--theme-destructive:focus-visible {
    box-shadow:
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring-destructive, var(--asc-focus-ring-error));
  }

  /* Destructive secondary: shadow-xs + white ring + error ring */
  .base--secondary.base--theme-destructive:focus-visible {
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring-destructive, var(--asc-focus-ring-error));
  }

  /* Destructive primary: shadow-xs + inset highlight + error ring (no white separator) */
  .base--primary.base--theme-destructive:focus-visible {
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      inset 0 0 0 2px rgba(255, 255, 255, 0.12),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring-destructive, var(--asc-focus-ring-error));
  }

  /* ═══════════════════════════════════════════════════════════
     SKEUOMORPHIC INNER SHADOW (::after overlay)
     Applied when enabled (not disabled/loading) on primary and
     secondary. The ::after pseudo-element keeps shadows inside
     the button boundary without affecting the focus box-shadow.
     ═══════════════════════════════════════════════════════════ */
  .base--primary:not(:disabled):not([aria-disabled='true'])::after,
  .base--secondary:not(:disabled):not([aria-disabled='true'])::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    box-shadow:
      inset 0 0 1px 1px rgba(10, 13, 18, 0.05),
      inset 0 -1px 1px 1px rgba(10, 13, 18, 0.05);
  }

  /* Focused state: inner shadow shifts per Figma */
  .base--primary:focus-visible::after,
  .base--secondary:focus-visible::after {
    box-shadow:
      inset 0 0 0 1px rgba(10, 13, 18, 0.18),
      inset 0 -2px 1px 1px rgba(10, 13, 18, 0.05);
  }

  /* ═══════════════════════════════════════════════════════════
     DISABLED STATE
     Global base applies to all variants; per-variant rules then
     override the values that differ from the default grey style.
     ═══════════════════════════════════════════════════════════ */

  /* Global base: grey fill */
  .base:disabled,
  .base[aria-disabled='true'] {
    background-color: var(--asc-button-disabled-bg, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-button-disabled-border, var(--asc-border-disabled_subtle));
    color: var(--asc-button-disabled-fg, var(--asc-text-disabled));
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Secondary: white BG + subtle border + fg-disabled_subtle text */
  .base--secondary:disabled,
  .base--secondary[aria-disabled='true'] {
    background-color: var(--asc-bg-primary);
    border-color: var(--asc-border-disabled_subtle);
    color: var(--asc-fg-disabled_subtle);
  }

  /* Tertiary: transparent bg/border + fg-disabled_subtle text (lighter than global) */
  .base--tertiary:disabled,
  .base--tertiary[aria-disabled='true'] {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-fg-disabled_subtle);
  }

  /* Link variants: transparent + fg-disabled_subtle text */
  .base--link-color:disabled,
  .base--link-color[aria-disabled='true'],
  .base--link-gray:disabled,
  .base--link-gray[aria-disabled='true'] {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-fg-disabled_subtle);
  }

  /* Destructive overrides: all variants use fg-disabled (darker than subtle) */
  .base--theme-destructive:disabled,
  .base--theme-destructive[aria-disabled='true'] {
    color: var(--asc-fg-disabled);
  }

  /* ═══════════════════════════════════════════════════════════
     LOADING STATE
     pointer-events:none is the only global rule. Each variant
     re-establishes its own full appearance because the button
     also has aria-disabled=true during loading (which would
     otherwise apply the disabled styles above).
     ═══════════════════════════════════════════════════════════ */
  .base--loading {
    pointer-events: none;
  }

  /* Primary: brand-solid_hover BG (default) / error-solid BG (destructive) */
  .base--primary.base--theme-default.base--loading {
    background-color: var(--asc-button-primary-bg-hover, var(--asc-bg-brand-solid_hover));
    border-color: transparent;
    color: var(--asc-button-primary-fg, var(--asc-color-base-white));
  }

  .base--primary.base--theme-destructive.base--loading {
    background-color: var(--asc-button-primary-bg-destructive, var(--asc-bg-error-solid));
    border-color: transparent;
    color: var(--asc-button-primary-fg-destructive, var(--asc-color-base-white));
  }

  /* Secondary (default): hover bg + primary border + secondary text + dark spinner */
  .base--secondary.base--loading {
    background-color: var(--asc-button-secondary-bg-hover, var(--asc-bg-primary_hover));
    border-color: var(--asc-button-secondary-border, var(--asc-border-primary));
    color: var(--asc-button-secondary-fg, var(--asc-text-secondary));
    --asc-button-spinner-color: var(--asc-button-secondary-spinner, var(--asc-text-secondary));
  }

  /* Secondary (destructive): error-primary bg + error border + error text + error spinner */
  .base--secondary.base--theme-destructive.base--loading {
    background-color: var(--asc-button-secondary-bg-destructive-hover, var(--asc-bg-error-primary));
    border-color: var(--asc-button-secondary-border-destructive, var(--asc-border-error_subtle));
    color: var(--asc-button-secondary-fg-destructive, var(--asc-text-error-primary));
    --asc-button-spinner-color: var(--asc-button-secondary-spinner-destructive, var(--asc-text-error-primary));
  }

  /* Tertiary (default): hover bg + tertiary text + matching spinner */
  .base--tertiary.base--theme-default.base--loading {
    background-color: var(--asc-bg-primary_hover);
    color: var(--asc-button-tertiary-fg, var(--asc-text-tertiary));
    --asc-button-spinner-color: var(--asc-button-tertiary-fg, var(--asc-text-tertiary));
  }

  /* Tertiary (destructive): error-primary bg + error text + matching spinner */
  .base--tertiary.base--theme-destructive.base--loading {
    background-color: var(--asc-bg-error-primary);
    color: var(--asc-button-tertiary-fg-destructive, var(--asc-text-error-primary));
    --asc-button-spinner-color: var(--asc-button-tertiary-fg-destructive, var(--asc-text-error-primary));
  }

  /* Link-color (default): transparent + brand-secondary text */
  .base--link-color.base--theme-default.base--loading {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-button-link-color-fg, var(--asc-text-brand-secondary));
  }

  /* Link-color (destructive): transparent + error-primary text */
  .base--link-color.base--theme-destructive.base--loading {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-text-error-primary);
  }

  /* Link-gray: transparent + tertiary text (no theme variant) */
  .base--link-gray.base--loading {
    background-color: transparent;
    border-color: transparent;
    color: var(--asc-button-link-gray-fg, var(--asc-text-tertiary));
  }

  /* ═══════════════════════════════════════════════════════════
     SPINNER
     Two-layer SVG spinner matching the Figma asset exactly:
       - First SVG: full ring at 30% opacity (the track)
       - Second SVG: quarter arc at full opacity (rotating indicator)
     Both paths share --asc-button-spinner-color.
     ═══════════════════════════════════════════════════════════ */
  .spinner {
    display: inline-block;
    position: relative;
    width: 1.25em;
    height: 1.25em;
    flex-shrink: 0;
    animation: asc-button-spin 0.75s linear infinite;
  }

  .spinner svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  @keyframes asc-button-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ═══════════════════════════════════════════════════════════
     SLOT WRAPPERS & UTILITIES
     ═══════════════════════════════════════════════════════════ */
  .leading-icon,
  .trailing-icon {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
